import { useState, useEffect } from 'react'
import { get, post, put } from '../utils/api'
import { IconPlus, IconCalendarEvent, IconRobot, IconExternalLink, IconChevronLeft, IconEdit } from '@tabler/icons-react'

const STATUS_COLORS = { tentative: 'bg-yellow-lt', definite: 'bg-green-lt', cancelled: 'bg-red-lt' }
const TYPES = ['Conference', 'Wedding', 'Corporate Meeting', 'Social Event', 'Association', 'SMERF', 'Other']

export default function EventsPage() {
  const [events, setEvents] = useState([])
  const [selected, setSelected] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({})
  const [portalUrl, setPortalUrl] = useState(null)
  const [traceMsg, setTraceMsg] = useState(null)

  const load = () => get('/events').then(setEvents).catch(() => {}).finally(() => setLoading(false))
  useEffect(() => { load() }, [])

  const selectEvent = async (id) => {
    const data = await get(`/events/${id}`)
    setSelected(data)
    setShowForm(false)
    setPortalUrl(null)
    setTraceMsg(null)
  }

  const startNew = () => {
    setSelected(null)
    setForm({ event_name: '', company: '', contact_name: '', event_type: '', arrival_date: '', departure_date: '', room_count: '', rate: '', room_nights: '', food_bev_revenue: '', av_revenue: '', cutoff_date: '', attrition_pct: 20, block_code: '', notes: '' })
    setShowForm(true)
    setEditing(false)
  }

  const startEdit = () => {
    setForm({
      event_name: selected.event_name || '', company: selected.company || '', contact_name: selected.contact_name || '',
      event_type: selected.event_type || '', arrival_date: selected.arrival_date?.split('T')[0] || '', departure_date: selected.departure_date?.split('T')[0] || '',
      room_count: selected.room_count || '', rate: selected.rate || '', room_nights: selected.room_nights || '',
      food_bev_revenue: selected.food_bev_revenue || '', av_revenue: selected.av_revenue || '', status: selected.status || 'tentative',
      cutoff_date: selected.cutoff_date?.split('T')[0] || '', attrition_pct: selected.attrition_pct || 20, block_code: selected.block_code || '',
      notes: selected.notes || ''
    })
    setEditing(true)
    setShowForm(true)
  }

  const save = async () => {
    try {
      if (editing && selected?.id) {
        await put(`/events/${selected.id}`, form)
      } else {
        await post('/events', form)
      }
      setShowForm(false)
      load()
    } catch (err) { alert(err.message) }
  }

  const generateTraces = async () => {
    if (!selected) return
    try {
      const r = await post(`/autotraces/generate/${selected.id}`)
      setTraceMsg(`Generated ${r.count} autotrace tasks`)
      setTimeout(() => setTraceMsg(null), 3000)
    } catch (err) { alert(err.message) }
  }

  const generatePortal = async () => {
    if (!selected) return
    try {
      const r = await post('/guest-portal/generate', { event_id: selected.id, contact_id: selected.contact_id, email: selected.contact_email })
      setPortalUrl(r.url)
    } catch (err) { alert(err.message) }
  }

  const fmt = (v) => v ? `$${Number(v).toLocaleString()}` : '-'
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-'

  if (loading) return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>

  return (
    <div className="row g-4">
      {/* Left: Event List */}
      <div className="col-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h2 className="mb-0">Events & Groups</h2>
          <button className="btn btn-primary btn-sm d-flex align-items-center gap-1" onClick={startNew}><IconPlus size={16} /> New Event</button>
        </div>
        <div className="card">
          <div className="list-group list-group-flush">
            {events.map(e => (
              <button key={e.id} className={`list-group-item list-group-item-action ${selected?.id === e.id ? 'active' : ''}`} onClick={() => selectEvent(e.id)}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-medium">{e.event_name}</div>
                    <div style={{ fontSize: '0.8rem' }} className="text-secondary">{e.company} {e.arrival_date && `- ${fmtDate(e.arrival_date)}`}</div>
                  </div>
                  <span className={`badge ${STATUS_COLORS[e.status] || 'bg-secondary-lt'}`}>{e.status}</span>
                </div>
              </button>
            ))}
            {!events.length && <div className="list-group-item text-center text-secondary py-4"><IconCalendarEvent size={32} className="mb-2" style={{ opacity: 0.3 }} /><br />No events yet</div>}
          </div>
        </div>
      </div>

      {/* Right: Detail or Form */}
      <div className="col-lg-8">
        {showForm ? (
          <div className="card">
            <div className="card-header d-flex justify-content-between">
              <h3 className="card-title mb-0">{editing ? 'Edit Event' : 'New Event'}</h3>
              <div className="d-flex gap-2">
                <button className="btn btn-primary btn-sm" onClick={save} disabled={!form.event_name}>Save</button>
                <button className="btn btn-ghost-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-6"><label className="form-label">Event Name</label><input className="form-control" value={form.event_name||''} onChange={e => setForm({...form, event_name: e.target.value})} /></div>
                <div className="col-6"><label className="form-label">Company</label><input className="form-control" value={form.company||''} onChange={e => setForm({...form, company: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Contact</label><input className="form-control" value={form.contact_name||''} onChange={e => setForm({...form, contact_name: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Event Type</label>
                  <select className="form-select" value={form.event_type||''} onChange={e => setForm({...form, event_type: e.target.value})}>
                    <option value="">Select...</option>{TYPES.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
                {editing && <div className="col-4"><label className="form-label">Status</label>
                  <select className="form-select" value={form.status||'tentative'} onChange={e => setForm({...form, status: e.target.value})}>
                    <option value="tentative">Tentative</option><option value="definite">Definite</option><option value="cancelled">Cancelled</option></select></div>}
                <div className="col-4"><label className="form-label">Arrival</label><input type="date" className="form-control" value={form.arrival_date||''} onChange={e => setForm({...form, arrival_date: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Departure</label><input type="date" className="form-control" value={form.departure_date||''} onChange={e => setForm({...form, departure_date: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Room Count</label><input type="number" className="form-control" value={form.room_count||''} onChange={e => setForm({...form, room_count: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Rate $/night</label><input type="number" className="form-control" value={form.rate||''} onChange={e => setForm({...form, rate: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Room Nights</label><input type="number" className="form-control" value={form.room_nights||''} onChange={e => setForm({...form, room_nights: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">F&B Revenue</label><input type="number" className="form-control" value={form.food_bev_revenue||''} onChange={e => setForm({...form, food_bev_revenue: e.target.value})} /></div>

                <div className="col-12"><hr style={{ margin: '4px 0' }} /><div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Room Block</div></div>
                <div className="col-4"><label className="form-label">Block Code</label><input className="form-control" value={form.block_code||''} onChange={e => setForm({...form, block_code: e.target.value})} placeholder="e.g. CREST0426" /></div>
                <div className="col-4"><label className="form-label">Cutoff Date</label><input type="date" className="form-control" value={form.cutoff_date||''} onChange={e => setForm({...form, cutoff_date: e.target.value})} /></div>
                <div className="col-4"><label className="form-label">Attrition %</label><input type="number" className="form-control" value={form.attrition_pct||20} onChange={e => setForm({...form, attrition_pct: e.target.value})} min="0" max="100" /></div>

                <div className="col-12"><label className="form-label">Notes</label><textarea className="form-control" rows="2" value={form.notes||''} onChange={e => setForm({...form, notes: e.target.value})} /></div>
              </div>
            </div>
          </div>
        ) : selected ? (
          <div>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <div>
                <h2 className="mb-1">{selected.event_name}</h2>
                <span className="text-secondary">{selected.company} - {selected.event_type}</span>
              </div>
              <div className="d-flex gap-2">
                <button className="btn btn-ghost-primary btn-sm d-flex align-items-center gap-1" onClick={startEdit}><IconEdit size={14} /> Edit</button>
                <button className="btn btn-ghost-primary btn-sm d-flex align-items-center gap-1" onClick={generateTraces}><IconRobot size={14} /> Autotraces</button>
                <button className="btn btn-ghost-primary btn-sm d-flex align-items-center gap-1" onClick={generatePortal}><IconExternalLink size={14} /> Portal Link</button>
              </div>
            </div>

            {traceMsg && <div className="alert alert-success py-2 mb-3" style={{ fontSize: '0.85rem' }}>{traceMsg}</div>}
            {portalUrl && (
              <div className="alert alert-info py-2 mb-3" style={{ fontSize: '0.85rem' }}>
                Guest Portal: <a href={portalUrl} target="_blank" rel="noopener noreferrer">{portalUrl}</a>
                <button className="btn btn-sm btn-ghost-primary ms-2" onClick={() => { navigator.clipboard.writeText(portalUrl) }}>Copy</button>
              </div>
            )}

            <div className="card mb-3">
              <div className="card-body">
                <div className="row g-2" style={{ fontSize: '0.9rem' }}>
                  <div className="col-4"><strong>Arrival:</strong> {fmtDate(selected.arrival_date)}</div>
                  <div className="col-4"><strong>Departure:</strong> {fmtDate(selected.departure_date)}</div>
                  <div className="col-4"><strong>Status:</strong> <span className={`badge ${STATUS_COLORS[selected.status]}`}>{selected.status}</span></div>
                  <div className="col-4"><strong>Contact:</strong> {selected.contact_name || '-'}</div>
                  <div className="col-4"><strong>Rooms:</strong> {selected.room_count}</div>
                  <div className="col-4"><strong>Rate:</strong> {fmt(selected.rate)}/night</div>
                  <div className="col-4"><strong>Room Nights:</strong> {selected.room_nights || '-'}</div>
                  <div className="col-4"><strong>F&B:</strong> {fmt(selected.food_bev_revenue)}</div>
                  <div className="col-4"><strong>AV:</strong> {fmt(selected.av_revenue)}</div>
                </div>
              </div>
            </div>

            {/* Room Block Info */}
            {(selected.block_code || selected.cutoff_date) && (
              <div className="card mb-3">
                <div className="card-header"><h4 className="card-title mb-0">Room Block</h4></div>
                <div className="card-body">
                  <div className="row g-2" style={{ fontSize: '0.9rem' }}>
                    <div className="col-4"><strong>Block Code:</strong> {selected.block_code || '-'}</div>
                    <div className="col-4"><strong>Cutoff Date:</strong> {fmtDate(selected.cutoff_date)}</div>
                    <div className="col-4"><strong>Attrition:</strong> {selected.attrition_pct || 20}%</div>
                  </div>
                </div>
              </div>
            )}

            {selected.notes && (
              <div className="card">
                <div className="card-header"><h4 className="card-title mb-0">Notes</h4></div>
                <div className="card-body" style={{ fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>{selected.notes}</div>
              </div>
            )}
          </div>
        ) : (
          <div className="card"><div className="card-body text-center text-secondary py-5">
            <IconCalendarEvent size={48} className="mb-3" style={{ opacity: 0.3 }} />
            <p>Select an event or create a new one</p>
          </div></div>
        )}
      </div>
    </div>
  )
}
